import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { ILightWeightObject, IPublicationSetting } from '@fi-shared/models';
import { MediaBankService } from './media-bank.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { AccessRightFiltersComponent, AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import { ModuleType } from '@fi-shared/types/module-type';
import { AppTextService } from '@fi-shared/app-text';
import { IMediaBankFile } from '@fi-shared/media-bank';
import { IMediaBankFileUploadErrorItem } from '@fi-shared/media-bank/media-bank';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { NgModel } from '@angular/forms';
import { ContentChildren, Directive, ElementRef, Injector, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { fiHasAccessToApplicationType } from '@fi-shared/utils';
import { AccessRightType, ApplicationSettingsType, FiCommentSettingType, PublicationType } from '@fi-shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiTagsService } from '@fi-shared/fi-shared-services';
import { YesNoPipe } from '@fi-app/utilities/fi-pipes/yes-no.pipe';
import { FiPageCollapsableContentComponent } from '@fi-app/components/fi-page-collapsable-content/fi-page-collapsable-content.component';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { ILightWeightTag } from '@fi-shared/fi-tag2/fi-tag2';

@Directive()
export class MediaBankEditBase {
    AccessRightFilterSettings = new AccessRightFilterSettings(ModuleType.MediaBank, false, true, ObjectTypes.MediaBank);
    EditTabs: EditTabsModel;
    ModuleType = ModuleType;
    uploadErrors: IMediaBankFileUploadErrorItem[] = [];
    selectedLanguageId: number;
    protected defaultContentLanguageId: number;
    hasRightsToAddTags: boolean = false;
    @ContentChildren(NgModel, { descendants: true }) inputs: QueryList<NgModel>;
    //optionDisabledFn = fiDoesLightWeightObjectHaveChildren;

    // https://chainformation.atlassian.net/browse/FI-6016
    // allow tags select on any level
    optionDisabledFn = (item: ILightWeightTag, array: ILightWeightTag[]) => item.IsDisabled; // user has no access to tag

    availableTags: ILightWeightTag[] = [];
    selectedSelectionTitle: string;
    @ViewChildren(FiPageCollapsableContentComponent) rollupContents: QueryList<FiPageCollapsableContentComponent>;
    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;
    @ViewChild('scrollableElement', { static: false }) scrollableElementRef: ElementRef<HTMLElement>;
    @ViewChild('accessRightFiltersContainerElement', { static: false })
    accessRightFiltersContainerElement: ElementRef<HTMLElement>;

    appTextService: AppTextService;
    mediaBankService: MediaBankService;
    fiDialogsService: FiDialogsService;
    private snackBar: FiSnackBarService;
    private contentLanguageSharedService: FiContentLanguageSharedService;
    private fiUserDataService: FiUserDataService;
    private fiTagsService: FiTagsService;
    private yesNoPipe: YesNoPipe;
    private scrollToElementAnimateService: FiScrollToElementAnimateService;

    constructor(injector: Injector) {
        this.appTextService = injector.get(AppTextService);
        this.mediaBankService = injector.get(MediaBankService);
        this.fiDialogsService = injector.get(FiDialogsService);
        this.snackBar = injector.get(FiSnackBarService);
        this.contentLanguageSharedService = injector.get(FiContentLanguageSharedService);
        this.fiUserDataService = injector.get(FiUserDataService);
        this.fiTagsService = injector.get(FiTagsService);
        this.yesNoPipe = injector.get(YesNoPipe);
        this.scrollToElementAnimateService = injector.get(FiScrollToElementAnimateService);
    }

    protected onInit(title: string) {
        const userData = this.fiUserDataService.getUserData();

        this.selectedLanguageId = userData.PreferredContentLanguage || 0;
        this.defaultContentLanguageId = this.contentLanguageSharedService.getDefaultContentLanguageId();
        this.EditTabs = new EditTabsModel(title);

        this.ModuleType = ModuleType;
        this.hasRightsToAddTags = fiHasAccessToApplicationType(
            ApplicationSettingsType.Tag,
            AccessRightType.Write,
            userData.ModuleAccessRights,
        );
        this.getAvailableTags();
        setTimeout(() => this.expandSettings());
    }

    expandSettings() {
        if (this.rollupContents)
            this.rollupContents.forEach((rollupContent: FiPageCollapsableContentComponent) =>
                rollupContent.expandContent(),
            );
    }

    private getAvailableTags(): void {
        this.TagsDataSource().then((response: ILightWeightTag[]) => {
            this.availableTags = response;
        });
    }

    isCommentsAllowed(commentsSettingType: FiCommentSettingType): boolean {
        return commentsSettingType === FiCommentSettingType.AllowAllComments;
    }

    updateSelectedSelectionTitle(selectionTitle: string) {
        this.selectedSelectionTitle = selectionTitle;
    }

    getShareText(publicationSetting: IPublicationSetting): string {
        let result = this.yesNoPipe.transform(publicationSetting.IsShared);
        if (publicationSetting.IsShared) {
            result +=
                ', ' +
                this.appTextService
                    .get(
                        publicationSetting.PublicationType === PublicationType.Continous
                            ? 'common.continuous'
                            : 'common.time_limited',
                    )
                    .toLowerCase();
        }
        return result;
    }

    addNewTag(tagName: string, callback: (tag: ILightWeightTag) => void): void {
        this.fiTagsService
            .saveTag(this.fiTagsService.getNewTagModel(tagName, ModuleType.MediaBank))
            .then((id: number) => {
                const newTag: ILightWeightTag = {
                    Id: id,
                    Name: tagName,
                    ParentId: null,
                    Level: 0,
                };

                callback(newTag);
            });
    }

    getDefaultLanguageIndex(names: IContentLanguageLabelModel[]): number {
        return names.findIndex((name: IContentLanguageLabelModel) => {
            return name.ContentLanguageId === this.defaultContentLanguageId;
        });
    }

    onValidateError(inputsWithErrors: NgModel[]): void {
        const valueAccessors = inputsWithErrors.map((ngModel) => ngModel.valueAccessor);
        const accessRightFiltersComponent: any = valueAccessors.find(
            (item) => item instanceof AccessRightFiltersComponent,
        );

        const errorMessage = accessRightFiltersComponent
            ? accessRightFiltersComponent.getErrorMessage()
            : this.appTextService.get('common.error_all_mandatory');

        if (valueAccessors.indexOf(this.accessRightFilters) !== -1) {
            this.expandSettings();
        }
        this.fiDialogsService.notify({
            messageText: errorMessage,
            headerText: this.appTextService.get('common.warning'),
        });
    }

    isNameRequired(names: IContentLanguageLabelModel[]): boolean {
        return !names.some((name: IContentLanguageLabelModel) => name.Value);
    }

    getNameInputPlaceholder(names: IContentLanguageLabelModel[]): string {
        return this.isNameRequired(names) ? this.appTextService.get('common.required') : '';
    }

    protected showSnackbar(isShared: boolean, mediaBankFilesLength: number): void {
        let textId: string;
        if (isShared) {
            textId =
                mediaBankFilesLength > 1 ? 'mediabank.mediabank_published_multiple' : 'mediabank.mediabank_published';
        } else {
            textId = mediaBankFilesLength > 1 ? 'mediabank.mediabank_saved_multiple' : 'mediabank.mediabank_saved';
        }

        this.snackBar.showSnackbar({
            textMessage: this.appTextService.get(textId),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    TagsDataSource() {
        return this.mediaBankService.getMediaBankFileTagsByUserAccessRights();
    }

    getReasonCode(mediaBankFile: IMediaBankFile) {
        const currentUploadError = this.uploadErrors.find((item) => item.mediaBankFile === mediaBankFile);
        if (currentUploadError) return currentUploadError.reasonCode;
    }
}
